import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { TID } from "@/constants/testIds";
import { Boxes, Wallet, Activity, ShieldCheck, TrendingUp, AlertTriangle } from "lucide-react";
import { AIPanel } from "@/components/capital/AskAssetAI";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { displayStatus } from "@/lib/capitalLabels";

const HORIZON_COLOR = {
  "Immediate Attention": { bar: "#e11d48", pill: "bg-rose-50 text-rose-700 border-rose-200" },
  "12 Month Plan": { bar: "#d97706", pill: "bg-amber-50 text-amber-700 border-amber-200" },
  "24 Month Plan": { bar: "#004a6b", pill: "bg-blue-50 text-gbe border-blue-200" },
  "Monitor": { bar: "#94a3b8", pill: "bg-slate-100 text-slate-600 border-slate-200" },
};
const CONF_COLOR = { "High / Very High": "#059669", "Medium": "#d97706", "Low": "#e11d48" };

export function CapitalOverview() {
  const { client } = useCapital();
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    api.get(`/capital/overview`, { params: { client } })
      .then((r) => setOv(r.data))
      .finally(() => setLoading(false));
  }, [client]);

  if (loading || !ov) return <div className="h-64 bg-white border border-slate-200 rounded-lg shimmer" />;

  const totalRenewal = ov.horizons.reduce((s, h) => h.horizon !== "Monitor" ? s + h.budget : s, 0);
  const immediate = ov.horizons.find((h) => h.horizon === "Immediate Attention") || { count: 0, budget: 0 };
  const m12 = ov.horizons.find((h) => h.horizon === "12 Month Plan") || { count: 0, budget: 0 };
  const m24 = ov.horizons.find((h) => h.horizon === "24 Month Plan") || { count: 0, budget: 0 };

  const cards = [
    { key: "assets", label: "Assets Managed", value: fmtNumber(ov.asset_count), icon: Boxes },
    { key: "value", label: "Total Replacement Value", value: fmtCurrency(ov.total_value), icon: Wallet },
    { key: "immediate", label: "Immediate Budget", value: fmtCurrency(immediate.budget), icon: AlertTriangle, tone: "rose" },
    { key: "m12", label: "12 Month Budget", value: fmtCurrency(m12.budget), icon: Activity, tone: "amber" },
    { key: "m24", label: "24 Month Budget", value: fmtCurrency(m24.budget), icon: TrendingUp, tone: "blue" },
    { key: "total_renewal", label: "Total Renewal (24m)", value: fmtCurrency(totalRenewal), icon: Wallet },
    { key: "health", label: "Avg Health Score", value: ov.avg_health == null ? "Insufficient data" : ov.avg_health.toFixed(1), icon: Activity, hint: ov.avg_health == null ? "" : "/ 100" },
    { key: "confidence", label: "Avg Data Confidence", value: ov.avg_confidence.toFixed(1), icon: ShieldCheck, hint: "/ 100" },
  ];

  return (
    <>
      <AIPanel screen="portfolio" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          const toneCls = c.tone === "rose"
            ? "border-rose-200 bg-gradient-to-br from-rose-50 to-white"
            : c.tone === "amber"
            ? "border-amber-200 bg-gradient-to-br from-amber-50 to-white"
            : c.tone === "blue"
            ? "border-blue-200 bg-gradient-to-br from-blue-50 to-white"
            : "border-slate-200 bg-white";
          return (
            <div key={c.key} data-testid={TID.capitalKPI(c.key)} className={`rounded-lg p-5 border ${toneCls} card-hover`}>
              <div className="flex items-start justify-between">
                <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{c.label}</div>
                <Icon className="w-4 h-4 text-slate-400" />
              </div>
              <div className="mt-3 font-display text-2xl font-semibold text-slate-900 tabular-nums">
                {c.value}
                {c.hint && <span className="ml-1 text-sm font-normal text-slate-400">{c.hint}</span>}
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900">Budget by Planning Horizon</h3>
          <p className="text-xs text-slate-500 mb-4">Where the renewal spend lands over the next 24 months</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={ov.horizons} layout="vertical" margin={{ top: 10, right: 30, left: 40, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 12 }} tickFormatter={(v) => `$${Math.round(v/1000)}k`} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis type="category" dataKey="horizon" tick={{ fill: "#334155", fontSize: 12 }} axisLine={false} tickLine={false} width={120} tickFormatter={displayStatus} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                <Bar dataKey="budget" radius={[0, 4, 4, 0]} maxBarSize={26}>
                  {ov.horizons.map((h) => (
                    <Cell key={h.horizon} fill={HORIZON_COLOR[h.horizon].bar} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-2">
            {ov.horizons.map((h) => (
              <div key={h.horizon} className={`text-xs px-3 py-2 rounded-md border ${HORIZON_COLOR[h.horizon].pill}`}>
                <div className="font-medium">{displayStatus(h.horizon)}</div>
                <div className="tabular-nums opacity-80">{h.count} assets · {fmtCurrency(h.budget)}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900">Data Confidence</h3>
          <p className="text-xs text-slate-500 mb-4">Confidence banding across the portfolio</p>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={ov.confidence_bands} dataKey="count" nameKey="band" innerRadius={44} outerRadius={80} paddingAngle={2}>
                  {ov.confidence_bands.map((b) => (<Cell key={b.band} fill={CONF_COLOR[b.band] || "#94a3b8"} />))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </>
  );
}
